import type { CreateRoleRequest } from '@vlabs/api/artemy/model/CreateRoleRequest';
import type { PatchRoleRequest } from '@vlabs/api/artemy/model/PatchRoleRequest';
import validate from '@vlabs/shared/validators';
import { Button } from '@vlabs/uikit/components/controls/button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { Textarea } from '@vlabs/uikit/components/controls/textarea/Textarea';
import { useForm } from 'react-hook-form';

import { useUserFormTranslation } from './i18n';
import st from './RoleForm.module.sass';

export type CreateDefaultValues = Omit<CreateRoleRequest, 'permissions'>;
export type UpdateDefaultValues = Omit<PatchRoleRequest, 'permissions'>;

type RoleFormProps =
  {
    context: 'create';
    defaultValues?: CreateDefaultValues;
    onSubmit: (values: CreateDefaultValues) => void | Promise<void>;
    onCancel?: VoidFunction;
  }
  |
  {
    context: 'update';
    defaultValues?: UpdateDefaultValues;
    onSubmit: (values: UpdateDefaultValues) => void | Promise<void>;
    onCancel?: VoidFunction;
  };

export function RoleForm({
  defaultValues = undefined,
  context,
  onSubmit,
  onCancel = undefined,
}: RoleFormProps): JSX.Element {
  const { t } = useUserFormTranslation();
  const form = useForm({ defaultValues });

  return (
    <form
      onSubmit={form.handleSubmit(async (values) => {
        if (context === 'create') return onSubmit(values as CreateDefaultValues);
        return onSubmit(values);
      })}
    >
      <div
        className={st.RoleForm}
        data-testid="RoleForm"
      >
        <h5>{t('title', { context })}</h5>

        <div className={st.DataWrapper}>
          <div className={st.InputWrapper}>
            {/* FIXME GUI-3538: показать красную звездочку через required-состояние Input из кита */}
            <Input
              id="name"
              label={t('name')}
              {...form.register('name', {
                required: validate.required(),
                minLength: validate.minLength(3),
                maxLength: validate.maxLength(128),
                validate: validate.latinName(),
              })}
              errors={form.formState.errors}
            />
          </div>

          <div className={st.TextareaWrapper}>
            <Textarea
              id="description"
              label={t('description')}
              {...form.register('description', {
                maxLength: validate.maxLength(512),
              })}
              errors={form.formState.errors}
            />
          </div>
        </div>

        <div className={st.Actions}>
          <Button
            className={st.Button}
            type="submit"
          >
            {t('submit', { context })}
          </Button>

          <Button
            className={st.Button}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}
